import React, { useEffect, useState } from 'react';
import { Badge, Button, Input, Select, Slideover, Switch, Textarea } from '../../../components/UI';
import { InspoCandidate, InspoConsent, InspoFeatureInput, InspoJourney, useInspoAdmin } from '../../../services';

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const CONSENT_COLOR: Record<InspoConsent, 'blue' | 'zinc' | 'red'> = { featured: 'blue', anonymous: 'zinc', hidden: 'red' };
const TEXT_FIELDS: { key: 'founder_name' | 'portrait_url' | 'headline' | 'story' | 'quote' | 'quote_by'; label: string; long?: boolean }[] = [
    { key: 'founder_name', label: 'Founder name (shown as "Meet …")' },
    { key: 'portrait_url', label: 'Founder photo link (https, a real photo only)' },
    { key: 'headline', label: 'Headline (optional)' },
    { key: 'story', label: 'Their story (leave empty to use the facts from their numbers)', long: true },
    { key: 'quote', label: 'Testimonial quote', long: true },
    { key: 'quote_by', label: 'Quote by (e.g. "Amie Wouda, Wildcomb Designs")' }
];

// #region Field
const Field: React.FunctionComponent<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
    <label className="block space-y-1">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{label}</span>
        {children}
    </label>
);

// #region CandidateEditor
const CandidateEditor: React.FunctionComponent<{ c: InspoCandidate; onSave: (input: InspoFeatureInput) => Promise<unknown> }> = ({ c, onSave }) => {
    const f = c.feature;
    const [draft, setDraft] = useState<InspoFeatureInput>({
        consent: f?.consent ?? 'anonymous', journey: f?.journey ?? 'store', pinned: f?.pinned ?? false,
        founder_name: f?.founder_name ?? '', portrait_url: f?.portrait_url ?? '',
        headline: f?.headline ?? '', story: f?.story ?? '', quote: f?.quote ?? '', quote_by: f?.quote_by ?? '', consent_note: f?.consent_note ?? ''
    });
    const [saving, setSaving] = useState(false);
    const set = (patch: InspoFeatureInput) => setDraft((d) => ({ ...d, ...patch }));
    const consentChanged = draft.consent !== (f?.consent ?? 'anonymous');
    const needsNote = draft.consent === 'featured' && consentChanged && !String(draft.consent_note ?? '').trim();

    const [photos, setPhotos] = useState((f?.photo_urls ?? []).join('\n'));
    const save = async () => {
        setSaving(true);
        // Consent is sent only when it changes, so the recorded date marks when the brand agreed.
        const { consent, ...rest } = draft;
        const photo_urls = photos.split(/\s+/).map((p) => p.trim()).filter(Boolean);
        await onSave(consentChanged ? { ...rest, consent, photo_urls } : { ...rest, photo_urls });
        setSaving(false);
    };

    return (
        <div className="space-y-3 border-t border-gray-200 pt-3 dark:border-stone-700">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Show as">
                    <Select value={draft.consent} onChange={(e) => set({ consent: e.target.value as InspoConsent })}>
                        <option value="anonymous">Anonymous (default)</option>
                        <option value="featured">Featured: name, logo and designs</option>
                        <option value="hidden">Hidden</option>
                    </Select>
                </Field>
                <Field label="Journey">
                    <Select value={draft.journey} onChange={(e) => set({ journey: e.target.value as InspoJourney })}>
                        <option value="store">Store</option>
                        <option value="artist">Artist-led</option>
                    </Select>
                </Field>
            </div>
            {draft.consent === 'featured' && (
                <Field label="How did they agree? (required to feature)">
                    <Input value={draft.consent_note ?? ''} placeholder="e.g. Email from the founder, 30 Oct 2026" onChange={(e) => set({ consent_note: e.target.value })} />
                </Field>
            )}
            {TEXT_FIELDS.map((t) => (
                <Field key={t.key} label={t.label}>
                    {t.long
                        ? <Textarea rows={3} value={draft[t.key] ?? ''} onChange={(e) => set({ [t.key]: e.target.value })} />
                        : <Input value={draft[t.key] ?? ''} onChange={(e) => set({ [t.key]: e.target.value })} />}
                </Field>
            ))}
            <Field label="Their own photos: rooms, installs, studio (https links, one per line; the first is the cover)">
                <Textarea rows={3} value={photos} onChange={(e) => setPhotos(e.target.value)} />
            </Field>
            <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <Switch checked={!!draft.pinned} onChange={(v: boolean) => set({ pinned: v })} color="blue" />
                    Pin as the hero (featured only)
                </label>
                <Button color="blue" disabled={saving || needsNote} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Text shows only while the brand is featured. Anonymous brands never show their name, logo, designs or exact figures.</p>
        </div>
    );
};

// #region InspoAdminSlideover
/** Superadmin: choose who is featured. Exact numbers here are for staff only. */
export const InspoAdminSlideover: React.FunctionComponent<{ open: boolean; setOpen: (v: boolean) => void; onSaved: () => void }> = ({ open, setOpen, onSaved }) => {
    const { candidates, loading, list, save } = useInspoAdmin();
    const [openId, setOpenId] = useState<number | null>(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (open) list(); }, [open]);

    return (
        <Slideover open={open} setOpen={setOpen} slideoverTitle="Edit Inspo stories">
            <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Brands ranked by their last 90 days of customer sales. Feature a brand only after it has agreed. Wall Blush and Paperbloom are never shown.
                </p>
                {loading && <p className="text-sm text-gray-500">Loading…</p>}
                {candidates.map((c) => (
                    <div key={c.brand_id} className="rounded-xl border border-gray-200 p-3 dark:border-stone-700">
                        <button type="button" className="flex w-full items-center justify-between gap-2 text-left" onClick={() => setOpenId(openId === c.brand_id ? null : c.brand_id)}>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">{c.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {money(c.sales_90d)} last 90 days · {money(c.sales_lifetime)} lifetime · {c.customers.toLocaleString('en-US')} customers
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                {c.feature?.pinned && <Badge color="amber">Pinned</Badge>}
                                <Badge color={CONSENT_COLOR[c.feature?.consent ?? 'anonymous']}>{c.feature?.consent ?? 'anonymous'}</Badge>
                            </div>
                        </button>
                        {openId === c.brand_id && (
                            <CandidateEditor c={c} onSave={async (input) => { if (await save(c.brand_id, input)) onSaved(); }} />
                        )}
                    </div>
                ))}
            </div>
        </Slideover>
    );
};
